import makeUUID from "@shared/utils/helpers/makeUUID"
import { FilterVehicle } from "../../../../api/vehicles/filter"
import { AddedFleetEntry } from "../../../../api/fleet"
import { hasUnresolvedEntries } from "./utils"
import { TableEntry } from "./types"

export type ManuallyResolveEntriesState = {
  entries: ReadonlyArray<TableEntry>
  hasUnresolvedEntries?: boolean
  currentIndex?: number
}

type Data<T> = {
  index: number
  value: T
}

const updateEntryWithConfirmedVehicle = (entries: ReadonlyArray<TableEntry>, data: Data<FilterVehicle>) => {
  const vehicle = { ...data.value } as Omit<FilterVehicle, "id"> & { id?: string }
  const vehicleId = makeUUID(vehicle.id)
  delete vehicle.id
  const newEntries = entries.map((entry, i) =>
    i === data.index
      ? {
          ...entry,
          vehicleId,
          ...(vehicle as Omit<FilterVehicle, "id">),
        }
      : entry
  )
  return {
    entries: newEntries,
    hasUnresolvedEntries: hasUnresolvedEntries(newEntries),
  }
}

const updateEntry = <T>(entries: ReadonlyArray<TableEntry>, data: Data<T>, key: keyof AddedFleetEntry) =>
  entries.map((entry, i) =>
    i === data.index
      ? {
          ...entry,
          [key]: data.value,
        }
      : entry
  )

export type ManuallyResolveEntriesAction =
  | { type: "setMake"; data: Data<string> }
  | { type: "setModel"; data: Data<string> }
  | { type: "setFuelType"; data: Data<string> }
  | { type: "setVehicle"; data: Data<FilterVehicle> }
  | { type: "setHasUnresolvedEntries"; hasUnresolvedEntries: boolean }
  | { type: "setCurrentIndex"; index: number | undefined }

export const manuallyResolveEntriesReducer = (
  state: ManuallyResolveEntriesState,
  action: ManuallyResolveEntriesAction
): ManuallyResolveEntriesState => {
  switch (action.type) {
    case `setMake`:
      return {
        ...state,
        entries: updateEntry<string>(state.entries, action.data, `make`),
      }
    case `setModel`:
      return {
        ...state,
        entries: updateEntry<string>(state.entries, action.data, `model`),
      }
    case `setFuelType`:
      return {
        ...state,
        entries: updateEntry<string>(state.entries, action.data, `fuelType`),
      }
    case `setVehicle`:
      return {
        ...state,
        ...updateEntryWithConfirmedVehicle(state.entries, action.data),
      }
    case `setCurrentIndex`: {
      return {
        ...state,
        currentIndex: action.index,
      }
    }
    default:
      return state
  }
}
